import create from 'zustand';
import io from 'socket.io-client';
import { SOCKET_URL } from '../config';

let socket = null;

const signalListeners = new Set();
const readyListeners = new Set();
const acceptedListeners = new Set();
let pendingSignals = [];
let pendingReady = [];
let pendingAccepted = [];

function emitOrQueue(listeners, queue, data) {
  if (listeners.size === 0) {
    queue.push(data);
    return queue;
  }
  listeners.forEach((listener) => listener(data));
  return queue;
}

function subscribe(listeners, queue, listener) {
  listeners.add(listener);
  const queued = queue.splice(0, queue.length);
  queued.forEach((item) => listener(item));
  return () => listeners.delete(listener);
}

export const useSocketStore = create((set) => ({
  socket: null,
  connected: false,
  messages: {},
  onlineUsers: new Set(),
  typingUsers: {},

  initSocket: (token) => {
    if (socket?.connected) {
      return socket;
    }

    if (socket) {
      socket.disconnect();
      socket = null;
    }

    socket = io(SOCKET_URL || undefined, {
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: Infinity
    });

    socket.on('connect', () => {
      set({ socket, connected: true });
    });

    socket.on('disconnect', () => {
      set({ connected: false });
    });

    socket.on('user-online', (userId) => {
      set((state) => ({
        onlineUsers: new Set([...state.onlineUsers, userId])
      }));
    });

    socket.on('user-offline', (userId) => {
      set((state) => {
        const updated = new Set(state.onlineUsers);
        updated.delete(userId);
        return { onlineUsers: updated };
      });
    });

    socket.on('user-typing', (data) => {
      set((state) => ({
        typingUsers: {
          ...state.typingUsers,
          [data.conversationId]: data
        }
      }));
    });

    socket.on('user-stop-typing', (data) => {
      set((state) => {
        const updated = { ...state.typingUsers };
        delete updated[data.conversationId];
        return { typingUsers: updated };
      });
    });

    socket.on('new-message', (message) => {
      const conversationId = message.conversationId;
      if (!conversationId) return;
      set((state) => {
        const existing = state.messages[conversationId] || [];
        if (message.id && existing.some((item) => item.id === message.id)) {
          return state;
        }
        return {
          messages: {
            ...state.messages,
            [conversationId]: [...existing, message]
          }
        };
      });
    });

    socket.on('webrtc-signal', (data) => {
      pendingSignals = emitOrQueue(signalListeners, pendingSignals, data);
    });

    socket.on('webrtc-ready', (data) => {
      pendingReady = emitOrQueue(readyListeners, pendingReady, data);
    });

    socket.on('call-accepted', (data) => {
      pendingAccepted = emitOrQueue(acceptedListeners, pendingAccepted, data);
    });

    return socket;
  },

  subscribeSignals: (listener) => subscribe(signalListeners, pendingSignals, listener),
  subscribeReady: (listener) => subscribe(readyListeners, pendingReady, listener),
  subscribeAccepted: (listener) => subscribe(acceptedListeners, pendingAccepted, listener),

  addMessage: (conversationId, message) => {
    set((state) => {
      const existing = state.messages[conversationId] || [];
      if (message.id && existing.some((item) => item.id === message.id)) {
        return state;
      }
      return {
        messages: {
          ...state.messages,
          [conversationId]: [...existing, message]
        }
      };
    });
  },

  disconnectSocket: () => {
    if (socket) {
      socket.disconnect();
      socket = null;
    }
    pendingSignals = [];
    pendingReady = [];
    pendingAccepted = [];
    set({ socket: null, connected: false, onlineUsers: new Set(), typingUsers: {} });
  }
}));
